import { useQuery } from '@tanstack/react-query'
import { useContext, useEffect, useState } from 'react'
import { isAxiosError } from 'axios'
import dayjs from 'dayjs'
import styled from 'styled-components'

import { QUERY_KEY } from '../../constants'

import { toast } from '../../utils'

import { StoresContext } from '../../provider/StoreProvider'

import useParamId from '../../hooks/useParamId'
import { useUserInfo } from '../../hooks/useUserInfo'
import { useNavigate } from '../../hooks/useNavigate'

import { getUserProductDetail, getUserProductSettle } from '../../api/product'

import { Wrap } from '../../components/container/Wrap'
import { Content } from '../../components/container/Content'
import { CenterWrap, AlignCenterItem, ColumnItem, RowBetween } from '../../components/common'
import { Card } from '../../components/card/Card'
import { ProductInfoCard } from '../../components/card/ProductInfoCard'
import { Text } from '../../components/text'
import { DivideLine } from '../../components/divide'
import { FOOTER_MIN_HEIGHT } from '../../components/footer'
import { Header } from '../../components/header'
import { Spinner } from '../../components/spinner/Spinner'
import { RowData } from './components'

import { Color } from '../../theme/color'

import { ReactComponent as ArrowRightGrayIcon } from '../../assets/ico/common/arrow/right/arrow_right_gray_18.svg'
import { ReactComponent as ArrowDownRoundIcon } from '../../assets/ico/common/expand/expand_roundDown.svg'
import { ReactComponent as ArrowDownIcon } from '../../assets/ico/common/down_18.svg'

interface Accordion {
  sale: boolean
  amount: boolean
}

export function SettlementDetail() {
  const {
    commonStore: { setInvalidAccess },
  } = useContext(StoresContext)

  const [accordion, setAccordion] = useState<Accordion>({ sale: true, amount: true })

  const userProductId = useParamId(3)
  const { isLogin, isCheckingLogin } = useUserInfo()
  const navigate = useNavigate()

  const userProductDetail = useQuery({
    queryKey: [QUERY_KEY.userProduct, { userProductId }],
    queryFn: async () => await getUserProductDetail(userProductId as number),
    enabled: typeof userProductId === 'number',
  })
  const userProductSettle = useQuery({
    queryKey: [QUERY_KEY.userProductSettle, { userProductId }],
    queryFn: async () => await getUserProductSettle(userProductId as number),
    enabled: typeof userProductId === 'number',
  })

  useEffect(() => {
    if (!isLogin && !isCheckingLogin) setInvalidAccess(true)
  }, [isLogin, isCheckingLogin])

  useEffect(() => {
    if (userProductDetail.isError && userProductDetail.error) {
      setInvalidAccess(true)
      console.log(userProductDetail.error)
      // if (isAxiosError(userProductDetail.error)) {
      //   toast(userProductDetail.error.response?.data.message, userProductDetail.error)
      // }
    }
  }, [userProductDetail.isError, userProductDetail.error])

  useEffect(() => {
    if (userProductSettle.isError && userProductSettle.error) {
      setInvalidAccess(true)
      console.log(userProductSettle.error)
      // if (isAxiosError(userProductSettle.error)) {
      //   toast(userProductSettle.error.response?.data.message, userProductSettle.error)
      // }
    }
  }, [userProductSettle.isError, userProductSettle.error])

  useEffect(() => {
    if (!userProductDetail.data) return
    if (userProductDetail.data.status !== 'SETTLEMENT_FINISH') {
      setInvalidAccess(true)
    }
  }, [userProductDetail.data])

  if (
    userProductDetail.isPending ||
    userProductDetail.isError ||
    userProductSettle.isPending ||
    userProductSettle.isError ||
    typeof userProductId !== 'number'
  ) {
    return (
      <Wrap>
        <Header type="SUB" title="정산하기" />
        <CenterWrap>
          <Spinner />
        </CenterWrap>
      </Wrap>
    )
  }

  const goProductDetail = () => navigate(`/market/product/${userProductDetail.data.product.id}`)

  const toggleSaleAccordion = () => setAccordion((prevState) => ({ ...prevState, sale: !prevState.sale }))

  const toggleAmountAccordion = () => setAccordion((prevState) => ({ ...prevState, amount: !prevState.amount }))

  const isFirstSellProducts = [25, 61].includes(userProductDetail.data.productId)

  return (
    <Wrap>
      <Header type="SUB" title="정산내역 확인하기" />
      <StyledContent backgroundColor="gray_1">
        <ProductInfoWrap>
          {/* 작품 정보 */}
          <ProductInfoCard>
            <ProductInfoContent $gap={16}>
              <ProductImage>
                <img src={userProductDetail?.data?.product.imageUri} alt={userProductDetail?.data?.product.name} />
              </ProductImage>
              <ColumnItem $gap={4}>
                <Text size={18} weight={600}>
                  {userProductDetail?.data?.product.artist}
                </Text>
                <Text size={14} color="text_secondary">
                  {userProductDetail?.data?.product.name}
                </Text>
              </ColumnItem>
              <ProductDetailLink onClick={goProductDetail}>
                <Text size={14} weight={500} color="text_secondary">
                  작품 상세 보기
                </Text>
                <ArrowRightGrayIcon />
              </ProductDetailLink>
            </ProductInfoContent>
            <DivideLine />
            <ColumnItem>
              <SaleInfoToggleBox isActive={accordion.sale}>
                <Card $direction="column" $gap={16} $padding="16px" $radius={8} $background="gray_1">
                  <RowData
                    title="배정수량"
                    content={{
                      value: `${userProductDetail?.data?.totalShares.toLocaleString()}주`,
                      size: 18,
                      weight: 600,
                    }}
                  />
                  <RowData
                    title="세후청산금"
                    content={{
                      value: `${userProductSettle.data?.balance.toLocaleString()}원`,
                      size: 18,
                      weight: 600,
                    }}
                  />
                </Card>
                <ArrowDownRoundIcon onClick={toggleSaleAccordion} />
              </SaleInfoToggleBox>
              <Accordion isActive={accordion.sale}>
                <ColumnItem $gap={16}>
                  <RowData
                    title="배정공고일"
                    content={`${dayjs(userProductSettle.data?.allotmentDate).format('YYYY-MM-DD')}`}
                  />
                  <RowData
                    title="매각완료일"
                    content={`${dayjs(userProductSettle.data?.sellDate).format('YYYY-MM-DD')}`}
                  />
                  <RowData title="보유기간" content={`${userProductSettle.data?.holdingDays}일`} />
                  <RowData
                    title="증권수량"
                    content={`${userProductSettle.data?.totalIssuedShares.toLocaleString()}주`}
                  />
                  <RowData
                    title="모집총액"
                    content={`${userProductSettle.data?.totalIssuedPrice.toLocaleString()}원`}
                  />
                  <RowData
                    title={isFirstSellProducts ? '작품청산금' : '세전청산금'}
                    content={`${userProductSettle.data?.revenuePrice.toLocaleString()}원`}
                  />
                  {!isFirstSellProducts && (
                    <RowData
                      isChildren={true}
                      title="매각대금"
                      content={`${userProductSettle.data?.sellPrice.toLocaleString()}원`}
                    />
                  )}
                  <RowData
                    isChildren={true}
                    title="판매수수료(VAT포함)"
                    content={`${userProductSettle.data?.sellFee.toLocaleString()}원`}
                  />
                  <RowData
                    isChildren={true}
                    title="성과보수(VAT포함)"
                    content={`${userProductSettle.data?.carriedInterest.toLocaleString()}원`}
                  />
                  <RowData title="모집가액" content={`${userProductSettle.data?.offerPrice.toLocaleString()}원`} />
                  <RowData
                    title="주당 세전청산금"
                    content={`${userProductSettle.data?.sharePrice.toLocaleString()}원`}
                  />
                  <RowData
                    title="세전수익률"
                    content={{
                      value:
                        `${(userProductSettle.data?.revenueRate ?? 0) >= 0.0 ? '+' : '-'}` +
                        `${userProductSettle.data?.revenueRate ?? 0}%`,
                      color: (userProductSettle.data?.revenueRate ?? 0) >= 0.0 ? 'error' : 'primary_4',
                    }}
                  />
                </ColumnItem>
              </Accordion>
            </ColumnItem>
          </ProductInfoCard>
          {/* 세후청산금 산정 근거 */}
          <ProductInfoCard>
            <RowData title="배정금액" content={`${userProductSettle.data?.originalBalance.toLocaleString()}원`} />
            <RowData title="세전청산금" content={`${userProductSettle.data?.userRevenuePrice.toLocaleString()}원`} />
            <RowData title="배당소득세" content={`${userProductSettle.data?.otherIncomeTax.toLocaleString()}원`} />
            <RowData title="지방소득세" content={`${userProductSettle.data?.localTax.toLocaleString()}원`} />
            <DivideLine />
            <ColumnItem>
              <RowBetween>
                <Text size={18} weight={600}>
                  세후청산금
                </Text>
                <AmountInfoToggleBox isActive={accordion.amount}>
                  <Text size={18} weight={600} color="error">
                    {userProductSettle.data?.balance.toLocaleString()}원
                  </Text>
                </AmountInfoToggleBox>
              </RowBetween>
            </ColumnItem>
          </ProductInfoCard>
          <OtherIncomeTaxDescBox>
            <OtherIncomeTaxToggleBox $gap={2} onClick={toggleAmountAccordion} isActive={accordion.amount}>
              <Text size={14} weight={600} block={true}>
                📌 배당소득세 산정방식
              </Text>
              <ArrowDownIcon />
            </OtherIncomeTaxToggleBox>
            <Accordion isActive={accordion.amount}>
              <Text size={14} lineHeight={22} color="text_secondary">
                • 배정금액 = 모집가액 × 배정수량
                <br />• 세전청산금 = {isFirstSellProducts ? '작품청산금' : '(매각대금 – 판매수수료 – 성과보수)'} ÷
                증권수량 × 배정수량
                <br />
                • 배당소득 = 세전청산금 – 배정금액
                <br />
                • 배당소득세 = 배당소득 × 14%
                <br />
                • 지방소득세 = 배당소득세 × 10%
                <br />
                <br />
                ※ 배당 소득세 및 지방 소득세 모두 원단위는 절사합니다.
                <br />
                ※ 연간 금융소득(이자+배당) 2,000만 원 이하인 투자자는 원천징수로 납세가 종료됩니다.
                <br />※ 연간 금융소득(이자+배당)의 합이 연 2,000만원 초과시 종합과세가 적용됩니다. 이 경우 다른 소득과
                합산되어 더 높은 세율이 적용될 수 있으니, 다른 금융소득이 있으시다면 이 점을 꼭 확인해주세요.
              </Text>
            </Accordion>
          </OtherIncomeTaxDescBox>
        </ProductInfoWrap>
      </StyledContent>
      <BottomLabel>
        <Text size={14} weight={700} color="text_white">
          정산 완료 내역
        </Text>
      </BottomLabel>
    </Wrap>
  )
}

const StyledContent = styled(Content)`
  padding-bottom: ${FOOTER_MIN_HEIGHT}px;
`

const ProductInfoWrap = styled(ColumnItem)`
  gap: 16px;
  padding: 16px;
  background-color: ${Color.gray_1};
`

const ProductInfoContent = styled(AlignCenterItem)`
  position: relative;
`

const ProductImage = styled.div`
  overflow: hidden;
  width: 100px;
  aspect-ratio: 1/1.3;
  border-radius: 8px;
  > img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const ProductDetailLink = styled(AlignCenterItem)`
  position: absolute;
  right: 0;
  bottom: 0;
  border: none;
  outline: none;
  cursor: pointer;
`

const SaleInfoToggleBox = styled(ColumnItem)<{ isActive: boolean }>`
  gap: 8px;
  > svg {
    margin: 0 auto;
    transform: rotate(${({ isActive }) => (isActive ? '-180deg' : '-0')});
    transition: 0.5s;
    cursor: pointer;
  }
`

const Accordion = styled(ColumnItem)<{ isActive: boolean }>`
  overflow: hidden;
  max-height: ${({ isActive }) => (isActive ? '100vh' : 0)};
  transition: 0.5s;
  & > * {
    margin-top: 16px;
  }
`

const AmountInfoToggleBox = styled(AlignCenterItem)<{ isActive: boolean }>`
  gap: 8px;
  > svg {
    margin-left: auto;
    transform: rotate(${({ isActive }) => (isActive ? '-180deg' : '-0')});
    transition: 0.5s;
  }
`

const OtherIncomeTaxDescBox = styled(ColumnItem)`
  padding: 0 8px;
`

const OtherIncomeTaxToggleBox = styled(AlignCenterItem)<{ isActive: boolean }>`
  cursor: pointer;
  > svg {
    transform: rotate(${({ isActive }) => (isActive ? '-180deg' : '-0')});
    transition: 0.5s;
  }
`

const BottomLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 33px;
  background-color: ${Color.primary_7};
`
